import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, MagnifyingGlass, Crosshair, NotePencil, Certificate, ChalkboardTeacher, Rocket } from "@phosphor-icons/react";
import { useFetch } from "@/lib/useFetch";
import { RiskNotice } from "@/components/layout/PublicLayout";
import { Section, SectionHead, Loader } from "@/components/Blocks";
import { EventCard } from "@/components/EventCard";
import { AssetRing } from "@/components/AssetIcon";
import { fmtDate } from "@/lib/api";
import { GraduatesBadge, GraduatesNotice } from "@/components/IndicatorCard";
import { PriceCards } from "@/components/PriceCards";

const MARKETS = [["currency", "FX"], ["network", "Crypto"], ["gold", "Metals"], ["rising", "Indices"], ["candles", "Stocks"]];

const STATS = [["30 years", "of price-action research"], ["20 + 10 years", "back-tested and forward-tested"], ["1 min → 6 months", "every time frame tested"], ["5 markets", "in one full-day class"]];

const ProofStrip = () => {
  const { data: tile } = useFetch("/results-tile");
  const rows = tile?.enabled ? [...STATS, [tile.figure, tile.label]] : STATS;
  return (
    <div className={`rise mt-6 grid grid-cols-2 gap-3 ${tile?.enabled ? "sm:grid-cols-5" : "sm:grid-cols-4"}`} style={{ animationDelay: "260ms" }} data-testid="proof-strip">
      {rows.map(([n, l], i) => (
        <div key={n} className="rounded-md border border-white/10 bg-white/5 p-3" data-testid={`proof-tile-${i}`}>
          <div className="font-display text-xl font-bold leading-tight text-[#E0A21B] sm:text-2xl">{n}</div>
          <div className="mt-1 text-xs text-white">{l}</div>
          {i === 4 && <Link to="/how-we-measure" className="mt-1 block text-[11px] text-white/70 underline" data-testid="results-how-link">How we measure</Link>}
        </div>
      ))}
    </div>
  );
};

const TourLine = () => {
  const { data: t } = useFetch("/tour");
  if (!t || !t.cities) return null;
  return (
    <p className="rise mt-5 text-sm text-white/80" style={{ animationDelay: "300ms" }} data-testid="hero-tour-line">
      Touring {t.cities} cities across Canada · {fmtDate(t.first, t.next_timezone, { year: undefined })} – {fmtDate(t.last, "America/Toronto")} · Next class: {t.next_city}, {fmtDate(t.next_start, t.next_timezone, { weekday: "short" })}
      <Link to="/cities#fall-2027" className="mt-1 block text-xs font-semibold text-[#E0A21B] underline-offset-4 hover:underline" data-testid="hero-fall-line">Fall 2027 Return Tour registration is now open →</Link>
    </p>
  );
};

const Hero = () => (
  <section className="relative overflow-hidden bg-[#0B1B33] noise" data-testid="home-hero">
    <img src="/brand/hero_art.jpg" alt="Rocket launching along a rising candlestick chart with targets per time frame"
      className="absolute inset-y-0 right-0 hidden h-full w-[62%] object-cover object-left lg:block" />
    <div className="absolute inset-y-0 left-0 hidden w-[55%] bg-gradient-to-r from-[#0B1B33] via-[#0B1B33] to-transparent lg:block" />
    <div className="relative mx-auto grid max-w-7xl px-5 py-16 lg:grid-cols-12 lg:px-8 lg:py-24">
      <div className="lg:col-span-6">
        <div className="eyebrow rise text-white/60" data-testid="hero-eyebrow">FOREXPERTS AI INC. PRESENTS</div>
        <h1 className="rise mt-4 font-display text-6xl font-black italic uppercase leading-[0.88] tracking-tight sm:text-7xl lg:text-8xl" style={{ animationDelay: "80ms" }}>
          <span className="chrome-text">Mission<br />Control</span>
        </h1>
        <div className="rise mt-3 flex items-center gap-4" style={{ animationDelay: "140ms" }}>
          <span className="h-px w-10 bg-[#E0A21B]" />
          <span className="font-display text-3xl font-bold uppercase tracking-[0.5em] text-[#E0A21B]">Trading</span>
          <span className="h-px flex-1 bg-[#E0A21B]" />
        </div>
        <p className="rise mt-6 text-xl italic text-white sm:text-2xl" style={{ animationDelay: "200ms" }}>Plan the Launch. Respect the Landing.</p>
        <h2 className="rise mt-5 text-2xl font-bold leading-tight text-white lg:text-[30px]" style={{ animationDelay: "220ms" }} data-testid="hero-headline">30 Years of Market Research. Five Markets. One Day.</h2>
        <p className="rise mt-3 max-w-xl text-[17px] text-white/75" style={{ animationDelay: "240ms" }} data-testid="hero-subheadline">
          The complete classroom course in Forex, Crypto, Metals, Indices and Stocks, taught by an industry veteran in the financial markets.
        </p>
        <ProofStrip />
        <Link to="/mission-control" className="rise mt-5 flex items-start gap-3 rounded-md border border-[#E0A21B]/60 bg-[#0F2342] p-4 transition-colors hover:border-[#E0A21B]" style={{ animationDelay: "280ms" }} data-testid="hero-tool-callout">
          <Rocket size={22} weight="fill" className="mt-0.5 shrink-0 text-[#E0A21B]" />
          <span>
            <span className="flex flex-wrap items-center gap-2 text-sm font-bold uppercase tracking-wide text-white">NEW: The Mission Control Indicator <GraduatesBadge /></span>
            <span className="mt-1 block text-sm text-white/75">Built from three decades of research, it shows the take-profit target and stop for every time frame on one screen, so you can plan every trade before you take it.</span>
            <span className="mt-1 block text-sm font-semibold text-[#E0A21B]">See how it works →</span>
          </span>
        </Link>
        <GraduatesNotice dark className="rise mt-3" />
        <TourLine />
        <div className="rise mt-6 flex flex-wrap gap-3" style={{ animationDelay: "320ms" }}>
          <Link to="/cities" className="btn-gold" data-testid="hero-find-class-btn">Find a class in your city <ArrowRight weight="bold" /></Link>
          <Link to="/quiz" className="btn-ghost-dark" data-testid="hero-quiz-btn">Take the free trader-type quiz</Link>
        </div>
        <RiskNotice dark className="mt-8 max-w-md" />
      </div>
      <img src="/brand/hero_art.jpg" alt="" className="mt-10 w-full rounded-lg lg:hidden" />
    </div>
  </section>
);

const range = (rows) => {
  const [a, b] = [rows[0], rows[rows.length - 1]];
  if (rows.length === 1) return fmtDate(a.start_at, a.timezone, { weekday: "short" });
  const same = fmtDate(a.start_at, a.timezone, { day: undefined, year: undefined }) === fmtDate(b.start_at, b.timezone, { day: undefined, year: undefined });
  return `${fmtDate(a.start_at, a.timezone, { year: undefined })} – ${fmtDate(b.start_at, b.timezone, same ? { month: undefined, year: undefined } : { year: undefined })} · ${rows.length} classes`;
};

const CityFinder = () => {
  const { data } = useFetch("/schedule");
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const all = Object.values((data || []).reduce((m, s) => ({ ...m, [s.city_slug]: [...(m[s.city_slug] || []), s] }), {}));
  const groups = all.map((g) => { const open = g.filter((s) => s.registration_open); return open.length ? open : [{ ...g[0], returning: true }]; });
  const list = groups.filter((g) => g[0].city.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="dark-panel p-6 lg:p-8" data-testid="city-finder">
      <div className="eyebrow">City finder</div>
      <h3 className="mt-2 font-display text-3xl font-bold uppercase text-white">Where is your launch pad?</h3>
      <div className="mt-5 flex items-center gap-2 rounded-md border border-white/20 bg-[#0B1B33] px-3">
        <MagnifyingGlass className="text-white/50" size={20} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type your city…" data-testid="city-finder-input"
          className="w-full bg-transparent py-3 text-white placeholder:text-white/40 focus:outline-none" />
      </div>
      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-1">
        {list.map((g) => (
          <button key={g[0].city_slug} onClick={() => nav(`/cities/${g[0].city_slug}`)} data-testid={`city-finder-${g[0].city_slug}`}
            className="flex items-center justify-between gap-2 rounded-md border border-white/10 px-3 py-2.5 text-left text-sm text-white transition-colors hover:border-[#E0A21B] hover:text-[#E0A21B]">
            <span>{g[0].city}, {g[0].province}</span>
            <span className="font-mono-num text-right text-xs text-white/60" data-testid={`city-finder-date-${g[0].city_slug}`}>{g[0].returning ? `Returning ${fmtDate(g[0].start_at, g[0].timezone, { weekday: "short" })}` : range(g)}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const STEPS = [
  [ChalkboardTeacher, "One classroom day", "Learn all five markets and the Mission Control method, live, with a real instructor."],
  [Crosshair, "Targets per time frame", "Read the range pulldown and targets from 1 minute to 6 months."],
  [NotePencil, "Demo flight log", "Practise on a demo account for six weeks. No real money at risk.", true],
  [Certificate, "Quizzes, badges, certificate", "Pass the quizzes, earn Bronze to Gold badges and a verifiable certificate.", true],
];

const HomePricing = () => {
  const { data } = useFetch("/pricing");
  if (!data) return null;
  const p = data.prices;
  return <PriceCards vipBenefits={data.vip_benefits} tiers={[{ code: "early", name: "Early-bird", price_cents: p.early, on_sale: true }, { code: "regular", name: "Regular", price_cents: p.regular, on_sale: true },
    { code: "vip", name: "VIP", price_cents: p.vip, capacity: p.vip_capacity, on_sale: !!data.vip_benefits, reason: data.vip_benefits ? "" : "vip_benefits_missing" }]} />;
};

const TourSchedule = () => {
  const { data } = useFetch("/schedule");
  if (!data) return <Loader />;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="tour-schedule">
      {data.filter((s) => s.season !== "fall-2027").map((s, i) => (
        <Link key={`${s.city_slug}-${i}`} to={s.event_slug ? `/events/${s.event_slug}` : `/cities/${s.city_slug}`} className="panel flex items-center justify-between gap-3 p-4 transition-colors hover:border-[#E0A21B]" data-testid={`schedule-item-${s.city_slug}${s.label ? `-${i}` : ""}`}>
          <span><span className="block font-semibold text-[#0B1B33]">{s.city}, {s.province}{s.label ? ` · ${s.label}` : ""}</span><span className="text-sm text-[#475569]">{fmtDate(s.start_at, s.timezone, { weekday: "short" })}</span></span>
          <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold uppercase ${s.event_slug ? "bg-[#1E8E5A]/15 text-[#1E8E5A]" : "bg-[#E0A21B]/15 text-[#9A6B00]"}`}>{s.event_slug ? "Book now" : "Opens soon"}</span>
        </Link>
      ))}
    </div>
  );
};

export default function Home() {
  const { data: events, loading } = useFetch("/events?limit=6");
  return (
    <>
      <Hero />
      <section className="border-y border-white/10 bg-[#0F2342]">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-around gap-8 px-5 py-10 lg:px-8">
          {MARKETS.map(([icon, label]) => <AssetRing key={label} name={icon} label={label} />)}
        </div>
      </section>
      <Section tone="light">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <SectionHead eyebrow="Next launches" title="Upcoming classes" sub="Seats are limited in every city. Times shown in each city's local time." />
          <Link to="/cities" className="btn-outline mb-10" data-testid="home-all-cities-btn">All cities <ArrowRight /></Link>
        </div>
        {loading ? <Loader /> : (events || []).length === 0 ? <TourSchedule /> : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {(events || []).map((e, i) => <EventCard key={e.id} e={e} i={i} />)}
          </div>
        )}
      </Section>
      <Section tone="white">
        <SectionHead eyebrow="Pricing" title="One ticket. Everything for the day." sub="Prices in US dollars. Each city's early-bird date is shown on its class page." />
        <HomePricing />
      </Section>
      <Section tone="dark" className="relative noise">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <SectionHead dark eyebrow="How it works" title="From launch prep to landing" />
            <div className="grid gap-4 sm:grid-cols-2">
              {STEPS.map(([Icon, t, d, after], i) => (
                <div key={t} className="dark-panel rise p-5" style={{ animationDelay: `${i * 80}ms` }} data-testid={`how-step-${i}`}>
                  <Icon size={30} weight="duotone" className="text-[#E0A21B]" />
                  {after && <span className="mt-3 inline-block rounded-full bg-[#1E8E5A]/20 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-[#5FD39A]" data-testid={`how-step-after-${i}`}>Included after your class</span>}
                  <h3 className="mt-3 font-display text-xl font-bold uppercase text-white">{t}</h3>
                  <p className="mt-1.5 text-sm text-white/65">{d}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5"><CityFinder /></div>
        </div>
      </Section>
      <Section tone="white">
        <div className="grid items-center gap-10 lg:grid-cols-12">
          <img src="/brand/tour_poster.png" alt="Mission Control Trading Canadian tour poster" className="w-full rounded-lg shadow-xl lg:col-span-5" />
          <div className="lg:col-span-7">
            <SectionHead eyebrow="Canadian tour" title="Victoria to Halifax, and more" sub="Victoria, Vancouver, Calgary, Edmonton, Red Deer, Lethbridge, Saskatoon, Winnipeg, Windsor, London, Guelph-Kitchener, Hamilton, St. Catharines, Mississauga, North York, Markham, Toronto, Kingston, Ottawa and Halifax." />
            <div className="flex flex-wrap gap-3">
              <Link to="/mission-control" className="btn-navy" data-testid="home-program-btn">See the program</Link>
              <Link to="/free-seminar" className="btn-outline" data-testid="home-seminar-btn">Join a free intro seminar</Link>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}
